import React, { useState, useRef, useMemo, useCallback } from "react";
import { ContentHeader } from "@components";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import DataTable from "../components/datatable/DataTable";
import API from "../api";
import toastr from "toastr";

/* ------------------------------------------------------------------ */
/*  Types                                                               */
/* ------------------------------------------------------------------ */
interface PatientEntry {
  id: number;
  bill_id: number;
  bill_no: string;
  patient_id: number;
  patient_name: string;
  entry_date: string;
  created: string;
  net_amount: number;
  advance_amount: number;
  balance_amount: number;
  test_status: number; // 0=pending, 1=completed, 2=saved
}

type ActiveTab = "pending" | "report";

// Header mode per row:  0 = text only  |  1 = header_logo_1  |  2 = header_logo_2
type HeaderMode = 0 | 1 | 2;

/* ------------------------------------------------------------------ */
/*  Helper: format a date/timestamp as DD/MM/YYYY in Indian Standard
    Time (IST). Using "en-GB" because it reliably renders DD/MM/YYYY
    with "/" separators across environments (unlike "en-IN", whose
    separator/format can vary by JS engine/ICU data).                 */
/* ------------------------------------------------------------------ */
function formatDateIST(value?: string | number | Date | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata" });
}

/* ------------------------------------------------------------------ */
/*  Helper: client-side sort fallback.
    DataTable's internal sort state may pass an explicit sortBy/sortOrder
    (even on first load, before the user clicks any header) that overrides
    the function's default parameters — JS default params only kick in for
    `undefined`, not for an explicitly-passed value like "" or "asc". To
    guarantee correct ordering regardless of what DataTable sends, we
    normalize the params with `||` and re-sort the fetched page client-side
    as a safety net.                                                      */
/* ------------------------------------------------------------------ */
function sortRows<T extends Record<string, any>>(
  rows: T[],
  sortBy: string,
  sortOrder: string
): T[] {
  const key   = sortBy || "id";
  const order = sortOrder === "desc" ? -1 : 1;

  return [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * order;
    return String(av).localeCompare(String(bv)) * order;
  });
}


/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */
const Report: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = user?.permissions || {};

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: ActiveTab = searchParams.get('tab') === 'completed' ? 'report' : 'pending';
  const setActiveTab = (tab: ActiveTab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', tab === 'report' ? 'completed' : 'pending');
    setSearchParams(nextParams);
  };

  // Track which header mode is selected for each report row id
  const [headerMode, setHeaderMode] = useState<Record<number, HeaderMode>>({});

  const pendingTableRef = useRef<any>(null);
  const reportTableRef  = useRef<any>(null);

  /* ---------------------------------------------------------------- */
  /*  Fetch helpers                                                     */
  /* ---------------------------------------------------------------- */
  const fetchPending = async (
    page: number,
    search: string,
    sortByParam?: string,
    sortOrderParam?: string
  ) => {
    // Guard against falsy/explicit-but-empty values overriding our default
    // of newest-first — see sortRows() comment above.
    const sortBy    = sortByParam    || "id";
    const sortOrder = sortOrderParam || "desc";
    try {
      const res = await API.get(
        `/report/pending?page=${page}&pageSize=25&search=${search}&sortBy=${sortBy}&sortOrder=${sortOrder}`
      );
      const data = sortRows(res.data.data ?? [], sortBy, sortOrder);
      return { data, total: res.data.total ?? 0 };
    } catch {
      toastr.error("Failed to fetch pending entries");
      return { data: [], total: 0 };
    }
  };

  const fetchReport = async (
    page: number,
    search: string,
    sortByParam?: string,
    sortOrderParam?: string
  ) => {
    // FIXED: default is now guaranteed to be id DESC (newest first) no
    // matter what DataTable's internal sort state passes on initial load.
    const sortBy    = sortByParam    || "id";
    const sortOrder = sortOrderParam || "desc";
    try {
      const res = await API.get(
        `/report/report?page=${page}&pageSize=25&search=${search}&sortBy=${sortBy}&sortOrder=${sortOrder}`
      );
      const data = sortRows(res.data.data ?? [], sortBy, sortOrder);
      return { data, total: res.data.total ?? 0 };
    } catch {
      toastr.error("Failed to fetch reports");
      return { data: [], total: 0 };
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Action handlers                                                  */
  /* ---------------------------------------------------------------- */

  /** View Report (Test Entry form) */
  const handleViewReport = (id: number) => {
    navigate(`/report/add_test_entry/${id}`);
  };

  /**
   * View Receipt – passes header mode as query param.
   */
  const headerModeRef = useRef(headerMode);
  headerModeRef.current = headerMode; // keep ref in sync

  const handleViewReceipt = useCallback(
    (id: number) => {
      const mode = headerModeRef.current[id] ?? 0;
      navigate(`/report/test_receipt/${id}?header=${mode}`);
    },
    [navigate]
  );

  /** Delete */
  const handleDelete = async (id: number) => {
    if (!window.confirm("This report will be permanently deleted. Are you sure?"))
      return;
    try {
      await API.delete(`/report/delete/${id}`);
      toastr.success("Deleted successfully");
      pendingTableRef.current?.refetch();
    } catch {
      toastr.error("Something went wrong.");
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Header mode change handler (stable reference)                    */
  /* ---------------------------------------------------------------- */
  const handleHeaderModeChange = useCallback((id: number, value: HeaderMode) => {
    setHeaderMode((prev) => ({ ...prev, [id]: value }));
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Column definitions                                               */
  /* ---------------------------------------------------------------- */

  /** Pending tab columns */
  const pendingColumns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      {
        header: "Date",
        id: "date_col",
        cell: ({ row }: any) => formatDateIST(row.original.created),
      },
      { header: "Bill No", accessorKey: "bill_no" },
      { header: "Patient Name", accessorKey: "patient_name" },
      { header: "Bill Amount", accessorKey: "net_amount" },
      { header: "Paid Amount", accessorKey: "advance_amount" },
      {
        header: "Balance Amount",
        id: "balance",
        cell: ({ row }: any) =>
          (row.original.net_amount - row.original.advance_amount).toFixed(2),
      },
      {
        header: "Action",
        id: "actions",
        cell: ({ row }: any) => {
          const { id } = row.original;
          return (
            <div className="d-flex gap-2">
              {permissions.report && (
                <button
                  className="btn btn-light"
                  onClick={() => handleViewReport(id)}
                  title="Enter Test Results"
                >
                  Report
                </button>
              )}
              {permissions.delete_report && (
                <button
                  className="btn btn-light"
                  onClick={() => handleDelete(id)}
                  title="Delete"
                >
                  Delete
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [permissions]
  );

  /** Completed Reports tab columns */
  const reportColumns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      {
        header: "Date",
        id: "date_col",
        cell: ({ row }: any) =>
          row.original.entry_date ? formatDateIST(row.original.entry_date) : "",
      },
      { header: "Bill No", accessorKey: "bill_no" },
      { header: "Patient Name", accessorKey: "patient_name" },
      { header: "Amount", accessorKey: "net_amount" },
      {
        header: "Header",
        id: "header_select",
        cell: ({ row }: any) => {
          const id = row.original.id as number;
          return (
            <HeaderModeSelect
              rowId={id}
              headerModeRef={headerModeRef}
              onChange={handleHeaderModeChange}
            />
          );
        },
      },
      {
        header: "Action",
        id: "actions",
        cell: ({ row }: any) => {
          const { id } = row.original;
          return (
            <button
              className="btn btn-light"
              onClick={() => handleViewReceipt(id)}
              title="View Report"
            >
              View
            </button>
          );
        },
      },
    ],
    [handleHeaderModeChange, handleViewReceipt]
  );

  /* ---------------------------------------------------------------- */
  /*  Access Denied                                                     */
  /* ---------------------------------------------------------------- */
  if (!permissions.report) {
    return (
      <div>
        <ContentHeader title="Report" />
        <section className="content">
          <div className="container-fluid">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fas fa-lock fa-3x text-muted mb-3"></i>
                <h4 className="text-muted">Access Denied</h4>
                <p className="text-muted">You do not have permission to access reports.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <ContentHeader title="Report" />
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-body">
              {/* ---- Tab Nav ---- */}
              <ul className="nav nav-tabs" role="tablist">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "pending" ? "active" : ""}`}
                    onClick={() => setActiveTab("pending")}
                  >
                    <i className="bx bx-home me-1" /> Pending Test
                  </button>
                </li>
                {permissions.view_report && (
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === "report" ? "active" : ""}`}
                      onClick={() => setActiveTab("report")}
                    >
                      <i className="bx bx-microphone me-1" /> Completed Reports
                    </button>
                  </li>
                )}
              </ul>

              {/* ---- Tab Content ---- */}
              <div className="tab-content py-3">
                {/* Pending */}
                {activeTab === "pending" && (
                  <div className="table-responsive">
                    <DataTable
                      ref={pendingTableRef}
                      columns={pendingColumns}
                      queryFn={fetchPending}
                      queryKey={["report-pending"]}
                    />
                  </div>
                )}

                {/* Completed Reports */}
                {activeTab === "report" && permissions.view_report && (
                  <div className="table-responsive">
                    <DataTable
                      ref={reportTableRef}
                      columns={reportColumns}
                      queryFn={fetchReport}
                      queryKey={["report-list"]}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Extracted cell component                                            */
/* ------------------------------------------------------------------ */
const HeaderModeSelect: React.FC<{
  rowId: number;
  headerModeRef: React.MutableRefObject<Record<number, 0 | 1 | 2>>;
  onChange: (id: number, value: 0 | 1 | 2) => void;
}> = ({ rowId, headerModeRef, onChange }) => {
  const [localMode, setLocalMode] = useState<0 | 1 | 2>(
    headerModeRef.current[rowId] ?? 0
  );

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = Number(e.target.value) as 0 | 1 | 2;
    setLocalMode(val);
    onChange(rowId, val);
  };

  return (
    <select
      className="form-select form-select-sm"
      style={{ minWidth: 120 }}
      value={localMode}
      onChange={handleChange}
    >
      <option value={0}>No Header</option>
      <option value={1}>Header 1</option>
      <option value={2}>Header 2</option>
    </select>
  );
};

export default Report;